'use client'

import { NewsFavicon } from '@/components/NewsFavicon'
import { BiasDot, ReliabilityDots } from '@/components/ui/SourceMeta'
import { RelativeTime } from '@/utils/date'
import { Dialog, DialogPanel } from '@headlessui/react'
import { useCallback, useEffect, useState, useMemo } from 'react'
import {
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiCloseLine,
  RiExternalLinkLine,
  RiLock2Line,
} from 'react-icons/ri'

function getBiasCategory(bias) {
  if (bias < -6) return 'left'
  if (bias > 6) return 'right'
  return 'center'
}

const SORT_OPTIONS = [
  { value: 'recent', label: 'Recent' },
  { value: 'reliability', label: 'Reliable' },
]

const BIAS_FILTERS = [
  { value: 'left', label: 'L', fullLabel: 'Left', color: 'bg-blue-600' },
  { value: 'center', label: 'C', fullLabel: 'Center', color: 'bg-gray-500' },
  { value: 'right', label: 'R', fullLabel: 'Right', color: 'bg-red-600' },
]

export default function SourcesLightbox({ isOpen, onClose, sources = [], initialIndex = 0 }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [sortBy, setSortBy] = useState('recent')
  const [biasFilters, setBiasFilters] = useState(['left', 'center', 'right'])
  const [reliableOnly, setReliableOnly] = useState(false)
  const [freeOnly, setFreeOnly] = useState(false)

  const toggleBiasFilter = (value) => {
    setBiasFilters(prev => {
      if (prev.includes(value)) {
        if (prev.length === 1) return prev
        return prev.filter(v => v !== value)
      }
      return [...prev, value]
    })
  }

  // Filter and sort sources
  const filteredSources = useMemo(() => {
    let result = sources.filter((source) => source.image)

    // Filter by bias
    result = result.filter(source => {
      const bias = source.outlet?.bias ?? 0
      return biasFilters.includes(getBiasCategory(bias))
    })

    // Filter by reliability
    if (reliableOnly) {
      result = result.filter(source => (source.outlet?.reliability ?? 0) >= 44)
    }

    // Filter by paywall
    if (freeOnly) {
      result = result.filter(source => !source.outlet?.is_paywalled)
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === 'recent') {
        return new Date(b.published_date || 0) - new Date(a.published_date || 0)
      }
      return (b.outlet?.reliability ?? 0) - (a.outlet?.reliability ?? 0)
    })

    return result
  }, [sources, sortBy, biasFilters, reliableOnly, freeOnly])

  const sourcesWithImages = filteredSources

  const currentSource = sourcesWithImages[currentIndex]

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? sourcesWithImages.length - 1 : prev - 1))
  }, [sourcesWithImages.length])

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === sourcesWithImages.length - 1 ? 0 : prev + 1))
  }, [sourcesWithImages.length])

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrevious()
      if (e.key === 'ArrowRight') goToNext()
      if (e.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, goToPrevious, goToNext, onClose])

  // Reset index and filters when opening
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex)
      setBiasFilters(['left', 'center', 'right'])
      setReliableOnly(false)
      setFreeOnly(false)
      setSortBy('recent')
    }
  }, [isOpen, initialIndex])

  // Don't render if no sources at all
  if (sources.filter(s => s.image).length === 0) return null

  const hasResults = sourcesWithImages.length > 0

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Full-screen backdrop */}
      <div className="fixed inset-0 bg-black/95" aria-hidden="true" />

      <div className="fixed inset-0 flex flex-col">
        <DialogPanel className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 gap-4">
            {/* Left: count + filters */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-white/70 text-sm font-medium">
                {currentIndex + 1} / {sourcesWithImages.length}
              </span>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); setCurrentIndex(0) }}
                className="text-xs bg-white/10 border border-white/20 rounded px-2 py-1 text-white focus:outline-none"
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value} className="bg-gray-900">
                    {opt.label}
                  </option>
                ))}
              </select>

              {/* Bias filters */}
              <div className="flex gap-0.5">
                {BIAS_FILTERS.map(filter => {
                  const isActive = biasFilters.includes(filter.value)
                  return (
                    <button
                      key={filter.value}
                      onClick={() => { toggleBiasFilter(filter.value); setCurrentIndex(0) }}
                      className={`w-7 h-7 text-xs font-bold rounded transition-colors ${
                        isActive ? `${filter.color} text-white` : 'bg-white/10 text-white/50'
                      }`}
                      title={filter.fullLabel}
                    >
                      {filter.label}
                    </button>
                  )
                })}
              </div>

              {/* Reliable toggle */}
              <button
                onClick={() => { setReliableOnly(!reliableOnly); setCurrentIndex(0) }}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                  reliableOnly ? 'bg-green-600 text-white' : 'bg-white/10 text-white/50'
                }`}
              >
                Reliable
              </button>

              {/* Free toggle */}
              <button
                onClick={() => { setFreeOnly(!freeOnly); setCurrentIndex(0) }}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                  freeOnly ? 'bg-amber-600 text-white' : 'bg-white/10 text-white/50'
                }`}
              >
                Free
              </button>
            </div>

            {/* Right: close */}
            <button
              onClick={onClose}
              className="p-2 text-white/70 hover:text-white transition-colors flex-shrink-0"
              aria-label="Close"
            >
              <RiCloseLine className="w-6 h-6" />
            </button>
          </div>

          {/* Main image area */}
          <div className="flex-1 flex items-center justify-center px-4 relative min-h-0">
            {!hasResults ? (
              <div className="text-white/60 text-center">
                <p className="text-lg mb-2">No sources match your filters</p>
                <p className="text-sm">Try adjusting your filter settings</p>
              </div>
            ) : (
            <>
            {/* Previous button */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 p-2 text-white/50 hover:text-white transition-colors z-10"
              aria-label="Previous image"
            >
              <RiArrowLeftSLine className="w-10 h-10" />
            </button>

            {/* Image container */}
            <div className="relative max-w-5xl w-full h-full flex flex-col items-center justify-center">
              <img
                src={currentSource?.image}
                alt={currentSource?.title}
                className="max-h-[60vh] max-w-full object-contain rounded-sm"
              />

              {/* Source info overlay */}
              <a
                href={currentSource?.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 p-4 bg-white/10 backdrop-blur-sm rounded-sm max-w-2xl w-full hover:bg-white/15 transition-colors group flex items-start gap-3"
              >
                <NewsFavicon
                  src={`${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/public_assets/news-favicons/${currentSource?.outlet?.slug}.jpg`}
                  alt={currentSource?.outlet?.name}
                  faviconSize="sm"
                  className="flex-shrink-0 mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-white font-serif text-base md:text-lg font-semibold leading-snug">
                      {currentSource?.title}
                    </h3>
                    <div className="flex items-center gap-1 text-white/60 group-hover:text-white transition-colors flex-shrink-0">
                      <RiExternalLinkLine className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2">
                      <span className="text-white/70 text-sm">
                        {currentSource?.outlet?.name}
                      </span>
                      <BiasDot bias={currentSource?.outlet?.bias} />
                      <ReliabilityDots
                        reliability={currentSource?.outlet?.reliability}
                        inactiveClass="bg-white/20"
                      />
                    </div>
                    <span className="text-white/60 text-sm flex-shrink-0">
                      <RelativeTime date={currentSource?.published_date} />
                    </span>
                  </div>
                </div>
              </a>
            </div>

            {/* Next button */}
            <button
              onClick={goToNext}
              className="absolute right-4 p-2 text-white/50 hover:text-white transition-colors z-10"
              aria-label="Next image"
            >
              <RiArrowRightSLine className="w-10 h-10" />
            </button>
            </>
            )}
          </div>

          {/* Thumbnail strip */}
          {hasResults && (
          <div className="p-4">
            <div className="flex justify-center gap-2 overflow-x-auto no-scrollbar">
              {sourcesWithImages.map((source, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-sm overflow-hidden transition-all ${
                    idx === currentIndex
                      ? 'ring-2 ring-white opacity-100'
                      : 'opacity-50 hover:opacity-75'
                  }`}
                >
                  <img
                    src={source.image}
                    alt={source.title}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  )
}
